import type { TMassiveUpdateDates } from "@/app/[locale]/settings/page"
import type { Numberish, TBaseResponse } from "@/models/common.model"
import type { TDiscipline } from "@/models/discipline.model"
import type { HttpClient } from "@/services/http-common"
import { tryCatch } from "@/lib/try-catch"
import { DisciplineMapper } from "@/mappers/competition/discipline.mapper"
import ApiServiceFactory from "@/services/api-service-factory"

type TApiDisciplineSettings = {
  disciplineCode: string
  dateInfo: {
    startDate: string
    endDate: string
  }
  venues: {
    code: string
    description: {
      [key: string]: {
        long: string
        short: string
        language?: string
      }
    }
  }[]
}

export type TSettingsSummary = {
  updatedCount: number
  failures: string[]
}

type TVenueSettings = {
  disciplineCode: string
  venues: Numberish[]
}

type TApiDateSettingsRequest = {
  disciplineCode: string
  dateInfo: {
    startDate: string | undefined
    endDate: string | undefined
  }
}

export type TMassiveUpdateVenues = {
  rscCode: string[]
  venueCodes: string[]
}

export type TDisciplineSettingsResponse = TBaseResponse<TApiDisciplineSettings[]>
export type TDisciplineSettingResponse = TBaseResponse<TApiDisciplineSettings>

export class DisciplineSettingsService {
  private httpClient: HttpClient
  // private baseUrl: string = "discipline-settings"

  constructor(httpClient: HttpClient) {
    this.httpClient = httpClient
  }

  async getAll(): Promise<TDiscipline[]> {
    try {
      const response = await tryCatch(this.httpClient.get<TDisciplineSettingsResponse>(`${"discipline-settings"}/all`))

      const { data, error } = response

      if (error) {
        if (error.statusCode === 404) {
          // return an empty array when the discipline is not found
          console.log("Discipline not found")
          return []
        }

        throw error
      }
      console.log("Discipline settings data:", data)
      return DisciplineMapper.disciplineSettingsToDto(data)
    }
    catch (error) {
      console.log("Error al obtener las disciplinas:", error)
      return [] // Retornar array vacío en caso de error
    }
  }

  async getByDisciplineCode(disciplineCode: string): Promise<TDiscipline> {
    try {
      // Pad discipline code to 34 characters with dashes if needed
      const paddedDisciplineCode = disciplineCode.length < 34
        ? disciplineCode.padEnd(34, "-")
        : disciplineCode

      const response = await tryCatch(this.httpClient.get<TDisciplineSettingResponse>(`discipline-settings/config-details/${paddedDisciplineCode}`))
      const { data, error } = response

      if (error) {
        if (error.statusCode === 404) {
          // return an empty array when the discipline is not found
          console.log("Discipline not found")
          return {} as TDiscipline
        }

        throw error
      }

      // Check if data is an array (empty configuration) and return empty object
      if (Array.isArray(data) && data.length === 0) {
        console.log("No configuration found, returning empty discipline")
        return {} as TDiscipline
      }

      // Ensure we pass an object to the mapper
      const disciplineData = Array.isArray(data) ? data[0] : data
      console.log("Discipline settings data:", disciplineData)
      return DisciplineMapper.disciplineSettingToDto(disciplineData)
    }
    catch (error) {
      console.error("Error al obtener las disciplinas:", error)
      return {} as TDiscipline
    }
  }

  async updateDates(disciplineCode: string, settings: TApiDateSettingsRequest): Promise<TDiscipline[]> {
    try {
      const response = await tryCatch(this.httpClient.put<TDisciplineSettingResponse>(`${"discipline-settings"}/update-dates/${disciplineCode}`, settings))
      const { data, error } = response
      console.log("Response: ", response)
      if (error) {
        if (error?.statusCode === 404) {
          // return an empty array when the discipline is not found
          console.log("Discipline not found")
          return []
        }

        throw error
      }

      return DisciplineMapper.disciplineSettingsToDto([data])
    }
    catch (error) {
      console.log("Error al actualizar las fechas:", error)
      console.error("Error al actualizar las fechas:", error)
      return [] // Retornar array vacío en caso de error
    }
  }

  async updateMasiveDates(settings: TMassiveUpdateDates): Promise<TSettingsSummary[]> {
    const response = await tryCatch(this.httpClient.put<TDisciplineSettingResponse>(`${"discipline-settings"}/massive-update-dates`, settings))
    const { data, error } = response

    console.log(response)
    if (error) {
      throw new Error(error instanceof Error ? error.message : "Unknown error")
    }

    return data as unknown as TSettingsSummary[]
  }

  async updateVenues(disciplineCode: string, settings: TVenueSettings): Promise<TDiscipline[]> {
    const response = await tryCatch(this.httpClient.put<TDisciplineSettingResponse>(`${"discipline-settings"}/update-venues/${disciplineCode}`, settings))
    const { data, error } = response

    console.log(response)
    if (error) {
      if (error.statusCode === 404) {
        // return an empty array when the discipline is not found
        console.log("Discipline not found")
        return []
      }
      throw new Error(error instanceof Error ? error.message : "Unknown error")
    }

    return DisciplineMapper.disciplineSettingsToDto([data])
  }

  async updateMasiveVenues(settings: TMassiveUpdateVenues): Promise<TSettingsSummary[]> {
    const response = await tryCatch(this.httpClient.put<TDisciplineSettingResponse>(`${"discipline-settings"}/massive-update-venues`, settings))
    const { data, error } = response

    console.log(response)
    if (error) {
      throw new Error(error instanceof Error ? error.message : "Unknown error")
    }

    return data as unknown as TSettingsSummary[]
  }
}

export const disciplineSettingsService = ApiServiceFactory.createService(DisciplineSettingsService, "disciplineSettings")

export default DisciplineSettingsService
